import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InquiryInput } from '@/types'
import type { InquiryErrors } from '@/lib/inquiry-validation'

import { useRouter } from 'next/router'
import { useEffect, useId, useRef, useState } from 'react'
import { CircleAlert, CircleCheckBig, LoaderCircle, MessageCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button-link'
import { contactInfo } from '@/constants/site'

import {
    EMPTY_INQUIRY,
    INQUIRY_LIMITS,
    normalizeInquiry,
    validateInquiry
} from '@/lib/inquiry-validation'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type FieldName = Exclude<keyof InquiryInput, 'website'>

interface InquiryFormProps {
    title?: string
    description?: string
    className?: string
}

type FieldConfig = {
    name: FieldName
    label: string
    required?: boolean
    type?: 'text' | 'email' | 'tel'
    autoComplete?: string
    placeholder?: string
    ltr?: boolean
    wide?: boolean
}

const FIELDS: FieldConfig[] = [
    { name: 'fullName', label: 'نام و نام خانوادگی', required: true, autoComplete: 'name' },
    { name: 'company', label: 'نام شرکت', autoComplete: 'organization' },
    { name: 'email', label: 'ایمیل', required: true, type: 'email', autoComplete: 'email', placeholder: 'name@company.com', ltr: true },
    { name: 'phone', label: 'تلفن یا واتس‌اپ', type: 'tel', autoComplete: 'tel', placeholder: '+98 912 000 0000', ltr: true },
    { name: 'country', label: 'کشور مقصد', autoComplete: 'country-name', placeholder: 'مثلاً آلمان' },
    { name: 'productInterest', label: 'محصول مورد نظر', placeholder: 'مثلاً آویشن کوهی زاگرس' },
    { name: 'quantity', label: 'مقدار تقریبی', placeholder: 'مثلاً ۵۰۰ کیلوگرم در ماه', wide: true }
]

const inputClasses = 'w-full rounded-xl border border-input bg-card px-4 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground/60 focus:border-gold focus:outline-none focus:ring-4 focus:ring-gold/15 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/10'

export const InquiryForm: FC<InquiryFormProps> = ({
    title = 'فرم استعلام قیمت و سفارش عمده',
    description = 'مشخصات سفارش خود را بنویسید تا مشخصات فنی، پیش‌فاکتور و شرایط ارسال را برایتان بفرستیم.',
    className
}): ReactNode => {
    const router = useRouter()
    const baseId = useId()
    const successRef = useRef<HTMLDivElement>(null)
    const [values, setValues] = useState<InquiryInput>(EMPTY_INQUIRY)
    const [errors, setErrors] = useState<InquiryErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const fieldId = (name: keyof InquiryInput): string => `${baseId}-${name}`

    useEffect(() => {
        if (!router.isReady) return

        const product = router.query.product

        if (typeof product !== 'string' || !product.trim()) return

        setValues(current => {
            if (current.productInterest) return current

            return { ...current, productInterest: product.trim().slice(0, INQUIRY_LIMITS.productInterest) }
        })
    }, [router.isReady, router.query.product])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const update = (name: keyof InquiryInput) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value

        setValues(current => ({ ...current, [name]: value }))

        if (errors[name]) setErrors(current => ({ ...current, [name]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const input = normalizeInquiry(values)
        const found = validateInquiry(input)
        const invalid = Object.keys(found) as Array<keyof InquiryInput>

        if (invalid.length > 0) {
            setErrors(found)
            setStatus('error')
            setMessage('لطفاً موارد مشخص‌شده را کامل یا اصلاح کنید.')
            document.getElementById(fieldId(invalid[0]))?.focus()
            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/inquiry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input)
            })

            const payload = await response.json().catch(() => null) as { ok?: boolean, message?: string, errors?: InquiryErrors } | null

            if (response.ok && payload?.ok) {
                setValues(EMPTY_INQUIRY)
                setErrors({})
                setStatus('success')
                return
            }

            if (payload?.errors) setErrors(payload.errors)

            setStatus('error')
            setMessage(payload?.message ?? 'ارسال استعلام ممکن نشد. لطفاً دوباره تلاش کنید.')
        }
        catch {
            setStatus('error')
            setMessage('ارتباط برقرار نشد. اتصال اینترنت خود را بررسی کنید و دوباره تلاش کنید.')
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className={cn('flex flex-col items-center rounded-[2rem] border border-sand bg-card px-6 py-14 text-center shadow-[0_30px_70px_-50px_rgba(15,42,30,0.6)] focus:outline-none sm:px-10', className)}
            >
                <span className='flex size-16 items-center justify-center rounded-full bg-forest-700 text-gold'>
                    <CircleCheckBig aria-hidden='true' className='size-8' strokeWidth={1.6} />
                </span>

                <p className='mt-6 font-heading text-3xl font-bold text-forest-900'>
                    استعلام شما ثبت شد
                </p>

                <p className='mt-3 max-w-md text-sm leading-7 text-muted-foreground sm:text-base sm:leading-8'>
                    سپاس از اعتماد شما. کارشناسان ما به‌زودی با شما تماس می‌گیرند. برای پاسخ سریع‌تر می‌توانید در واتس‌اپ هم پیام دهید.
                </p>

                <div className='mt-8 flex flex-wrap justify-center gap-3'>
                    <a
                        href={contactInfo.whatsapp.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={buttonClasses('primary', 'md')}
                    >
                        <MessageCircle aria-hidden='true' />
                        گفتگو در واتس‌اپ
                    </a>

                    <button type='button' onClick={() => setStatus('idle')} className={buttonClasses('outline', 'md')}>
                        ارسال استعلام دیگر
                    </button>
                </div>
            </div>
        )
    }

    return (
        <form
            noValidate
            onSubmit={submit}
            aria-labelledby={`${baseId}-title`}
            className={cn('relative rounded-[2rem] border border-sand bg-card p-6 shadow-[0_30px_70px_-50px_rgba(15,42,30,0.6)] sm:p-9', className)}
        >
            <h2 id={`${baseId}-title`} className='text-3xl font-bold text-forest-900'>
                {title}
            </h2>

            <p className='mt-2 text-sm leading-7 text-muted-foreground'>
                {description}
            </p>

            <div className='mt-8 grid gap-5 sm:grid-cols-2'>
                {FIELDS.map(field => {
                    const id = fieldId(field.name)
                    const error = errors[field.name]

                    return (
                        <div key={field.name} className={cn('flex flex-col gap-2', field.wide && 'sm:col-span-2')}>
                            <label htmlFor={id} className='text-sm font-medium text-forest-900'>
                                {field.label}
                                {field.required && <span aria-hidden='true' className='ms-1 text-destructive'>*</span>}
                            </label>

                            <input
                                id={id}
                                name={field.name}
                                type={field.type ?? 'text'}
                                dir={field.ltr ? 'ltr' : undefined}
                                autoComplete={field.autoComplete}
                                placeholder={field.placeholder}
                                required={field.required}
                                maxLength={INQUIRY_LIMITS[field.name]}
                                value={values[field.name]}
                                onChange={update(field.name)}
                                aria-invalid={error ? true : undefined}
                                aria-describedby={error ? `${id}-error` : undefined}
                                className={cn(inputClasses, 'h-12', field.ltr && 'text-left')}
                            />

                            {error && (
                                <p id={`${id}-error`} className='text-xs text-destructive'>
                                    {error}
                                </p>
                            )}
                        </div>
                    )
                })}

                <div className='flex flex-col gap-2 sm:col-span-2'>
                    <label htmlFor={fieldId('message')} className='text-sm font-medium text-forest-900'>
                        توضیحات سفارش
                        <span aria-hidden='true' className='ms-1 text-destructive'>*</span>
                    </label>

                    <textarea
                        id={fieldId('message')}
                        name='message'
                        rows={5}
                        required
                        maxLength={INQUIRY_LIMITS.message}
                        placeholder='نوع بسته‌بندی، زمان تحویل، نیاز به نمونه یا برگه آنالیز و هر نکته دیگری را بنویسید.'
                        value={values.message}
                        onChange={update('message')}
                        aria-invalid={errors.message ? true : undefined}
                        aria-describedby={errors.message ? `${fieldId('message')}-error` : undefined}
                        className={cn(inputClasses, 'resize-y py-3 leading-7')}
                    />

                    {errors.message && (
                        <p id={`${fieldId('message')}-error`} className='text-xs text-destructive'>
                            {errors.message}
                        </p>
                    )}
                </div>

                <div aria-hidden='true' className='sr-only'>
                    <label htmlFor={fieldId('website')}>وب‌سایت</label>
                    <input
                        id={fieldId('website')}
                        name='website'
                        type='text'
                        tabIndex={-1}
                        autoComplete='off'
                        value={values.website}
                        onChange={update('website')}
                    />
                </div>
            </div>

            {status === 'error' && message && (
                <p role='alert' className='mt-6 flex items-start gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
                    {message}
                </p>
            )}

            <div className='mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-xs leading-6 text-muted-foreground'>
                    اطلاعات شما فقط برای پاسخ به همین استعلام استفاده می‌شود.
                </p>

                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className={buttonClasses('primary', 'lg', 'w-full sm:w-auto')}
                >
                    {status === 'submitting' ? (
                        <>
                            <LoaderCircle aria-hidden='true' className='animate-spin' />
                            در حال ارسال...
                        </>
                    ) : (
                        <>
                            <Send aria-hidden='true' className='-scale-x-100' />
                            ارسال استعلام
                        </>
                    )}
                </button>
            </div>
        </form>
    )
}
